import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Play, Clock, Headphones, Star, ChevronLeft, Share2, Download, BookOpen, Users, Zap, Music, AlertCircle } from 'lucide-react'
import PodcastCard from '../components/PodcastCard'
import PodcastPlayer from '../components/PodcastPlayer'
import { podcasts } from '../data/mockData'
import useStore from '../store/useStore'
import { hasAudioFile } from '../utils/audioFolder'

// Updated featured series data (use the same from PodcastHub.jsx)
const featuredSeries = [
  {
    id: 1,
    title: "AI Revolution Series",
    description: "From fundamentals to breakthroughs - comprehensive exploration of artificial intelligence",
    category: "Technology & AI",
    episodeCount: 5,
    totalDuration: "3.9 hours",
    rating: 4.9,
    color: "from-blue-500 to-purple-600",
    image: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=800&h=400&fit=crop",
    episodes: [204, 216, 217, 258, 259],
    learningPath: [
      "Start with AI fundamentals",
      "Understand the competitive landscape",
      "Navigate AI ethics and bias",
      "Explore future AI architectures",
      "Recognize corporate AI risks"
    ]
  },
  {
    id: 2,
    title: "Digital Psychology Series",
    description: "How technology is reshaping our minds, habits, and social connections",
    category: "Personal Development",
    episodeCount: 6,
    totalDuration: "5.2 hours",
    rating: 4.8,
    color: "from-purple-500 to-pink-600",
    image: "https://images.unsplash.com/photo-1543286386-713bdd548da4?w=800&h=400&fit=crop",
    episodes: [263, 289, 291, 319, 324, 328],
    learningPath: [
      "Understand dopamine and digital addiction",
      "Master modern communication patterns",
      "Overcome decision paralysis",
      "Address phone call anxiety",
      "Reclaim boredom for creativity",
      "Break autoplay compulsions"
    ]
  },
  {
    id: 3,
    title: "Future Tech Deep Dives",
    description: "Cutting-edge technologies that are reshaping our world tomorrow",
    category: "Science & Innovation",
    episodeCount: 5,
    totalDuration: "4.3 hours",
    rating: 4.7,
    color: "from-teal-500 to-green-600",
    image: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&h=400&fit=crop",
    episodes: [203, 205, 223, 246, 262],
    learningPath: [
      "Master neural network fundamentals",
      "Explore space technology innovations",
      "Understand quantum computing's present",
      "Discover mRNA medical breakthroughs",
      "Dive into quantum biology frontiers"
    ]
  },
  {
    id: 4,
    title: "Creative Science Series",
    description: "Where art, science, and innovation collide in unexpected ways",
    category: "Creative Arts",
    episodeCount: 5,
    totalDuration: "4.8 hours",
    rating: 4.8,
    color: "from-orange-500 to-red-600",
    image: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=800&h=400&fit=crop",
    episodes: [235, 266, 275, 287, 329],
    learningPath: [
      "Integrate AI into creative workflows",
      "Navigate AI copyright complexities",
      "Analyze speedrunning mathematics",
      "Engineer the perfect playlist",
      "Understand therapeutic game design"
    ]
  },
  {
    id: 5,
    title: "Health & Wellness Science",
    description: "Evidence-based approaches to physical and mental wellbeing",
    category: "Health & Wellness",
    episodeCount: 5,
    totalDuration: "4.1 hours",
    rating: 4.7,
    color: "from-green-500 to-emerald-600",
    image: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop",
    episodes: [252, 254, 257, 307, 316],
    learningPath: [
      "Evaluate modern weight loss science",
      "Explore sober-curious benefits",
      "Understand temperature therapy",
      "Address digital physical health",
      "Adapt to digital evolution"
    ]
  },
  {
    id: 6,
    title: "Business & Creator Economy",
    description: "Modern entrepreneurship and the new rules of digital business",
    category: "Business & Entrepreneurship",
    episodeCount: 5,
    totalDuration: "4.5 hours",
    rating: 4.8,
    color: "from-indigo-500 to-blue-600",
    image: "https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&h=400&fit=crop",
    episodes: [201, 224, 229, 260, 265],
    learningPath: [
      "Master digital marketing strategies",
      "Navigate creator economy shifts",
      "Build scalable solo businesses",
      "Monetize niche newsletters",
      "Implement four-day workweeks"
    ]
  },
  {
    id: 7,
    title: "Everyday Science Mysteries",
    description: "Solving life's little puzzles with rigorous scientific investigation",
    category: "Science & Innovation",
    episodeCount: 5,
    totalDuration: "3.5 hours",
    rating: 4.6,
    color: "from-yellow-500 to-orange-600",
    image: "https://images.unsplash.com/photo-1517976547714-720226b864c1?w=800&h=400&fit=crop",
    episodes: [214, 277, 280, 322, 272],
    learningPath: [
      "Decode scent marketing psychology",
      "Solve the sock disappearance mystery",
      "Master slow cooker thermodynamics",
      "Understand popcorn's explosive physics",
      "Analyze pastry food science"
    ]
  },
  {
    id: 8,
    title: "Digital Life & Identity",
    description: "Navigating the complexities of our online selves and communities",
    category: "Personal Development",
    episodeCount: 5,
    totalDuration: "4.9 hours",
    rating: 4.7,
    color: "from-cyan-500 to-blue-500",
    image: "https://images.unsplash.com/photo-1531297484001-80022131f5a1?w=800&h=400&fit=crop",
    episodes: [288, 293, 304, 305, 311],
    learningPath: [
      "Navigate AI grief technology",
      "Parent in the digital age",
      "Overcome digital hoarding",
      "Plan your digital afterlife",
      "Build meaningful online communities"
    ]
  }
]

function PodcastSeries() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { setCurrentlyPlaying, user } = useStore()
  const [currentPlayingEpisode, setCurrentPlayingEpisode] = useState(null)
  const [seriesEpisodes, setSeriesEpisodes] = useState([])

  const series = featuredSeries.find(s => s.id === parseInt(id))

  // Get episodes for this series with audio status
  useEffect(() => {
    if (series) {
      const episodes = series.episodes.map(episodeId => {
        const episode = podcasts.find(p => p.id === episodeId)
        if (episode) {
          const filename = episode.audioUrl?.split('/').pop() || ''
          const hasAudio = hasAudioFile(filename)
          return {
            ...episode,
            hasAudio,
            status: hasAudio ? 'ready' : 'coming-soon'
          }
        }
        return null
      }).filter(Boolean)
      
      setSeriesEpisodes(episodes)
      
      // Set first episode with audio as current playing, or first episode if none have audio
      const firstEpisodeWithAudio = episodes.find(ep => ep.hasAudio)
      setCurrentPlayingEpisode(firstEpisodeWithAudio || episodes[0])
    }
  }, [series])

  if (!series) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Series Not Found</h2>
          <button 
            onClick={() => navigate('/podcasts')}
            className="btn-primary"
          >
            Back to Podcasts
          </button>
        </div>
      </div>
    )
  }

  // Calculate series stats
  const seriesStats = {
    totalEpisodes: seriesEpisodes.length,
    episodesWithAudio: seriesEpisodes.filter(ep => ep.hasAudio).length,
    episodesReady: seriesEpisodes.filter(ep => ep.hasAudio).length,
    episodesComingSoon: seriesEpisodes.filter(ep => !ep.hasAudio).length,
    completionPercentage: seriesEpisodes.length > 0 
      ? Math.round((seriesEpisodes.filter(ep => ep.hasAudio).length / seriesEpisodes.length) * 100)
      : 0,
    seriesStatus: seriesEpisodes.filter(ep => ep.hasAudio).length === seriesEpisodes.length ? 'ready' :
                 seriesEpisodes.filter(ep => ep.hasAudio).length > 0 ? 'partial' : 'coming-soon'
  }

  const handlePlayEpisode = (episode) => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', `/podcast-series/${id}`)
      navigate('/auth')
      return
    }
    
    if (!episode.hasAudio) {
      navigate(`/podcast/coming-soon/${episode.id}`, { 
        state: { 
          podcast: episode,
          missingAudio: true 
        } 
      })
      return
    }
    
    setCurrentlyPlaying(episode)
    setCurrentPlayingEpisode(episode)
  }

  const handlePlaySeries = () => {
    if (seriesEpisodes.length > 0) {
      const firstEpisodeWithAudio = seriesEpisodes.find(ep => ep.hasAudio)
      if (firstEpisodeWithAudio) {
        handlePlayEpisode(firstEpisodeWithAudio)
      } else {
        // If no episodes have audio, send to coming soon for first episode
        navigate(`/podcast/coming-soon/${seriesEpisodes[0].id}`, { 
          state: { 
            podcast: seriesEpisodes[0],
            missingAudio: true 
          } 
        })
      }
    }
  }

  const handleShareSeries = async () => {
    const shareData = {
      title: series.title,
      text: series.description,
      url: window.location.href,
    }
    
    if (navigator.share) {
      try {
        await navigator.share(shareData)
      } catch (err) {
        console.log('Error sharing:', err)
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
      alert('Link copied to clipboard!')
    }
  }

  const handleCourseNavigation = (courseId) => {
    if (!user) {
      localStorage.setItem('lastVisitedPage', `/podcast-series/${id}`)
      navigate('/auth')
      return
    }
    navigate(`/course/${courseId}`)
  }

  // Get status badge for episode
  const getEpisodeStatusBadge = (episode) => {
    if (episode.hasAudio) {
      return (
        <div className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
          <Music className="w-3 h-3" />
          Ready
        </div>
      )
    } else {
      return (
        <div className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          Coming Soon
        </div>
      )
    }
  }

  // Get series status badge
  const getSeriesStatusInfo = () => {
    switch(seriesStats.seriesStatus) {
      case 'ready':
        return {
          color: 'bg-green-500',
          text: 'All Episodes Ready',
          icon: <Music className="w-4 h-4" />
        }
      case 'partial':
        return {
          color: 'bg-amber-500',
          text: `${seriesStats.episodesReady}/${seriesStats.totalEpisodes} Episodes Ready`,
          icon: <AlertCircle className="w-4 h-4" />
        }
      default:
        return {
          color: 'bg-gray-500',
          text: 'Coming Soon',
          icon: <AlertCircle className="w-4 h-4" />
        }
    }
  }

  const seriesStatusInfo = getSeriesStatusInfo()

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className={`bg-gradient-to-br ${series.color} text-white py-16`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <button
                onClick={() => navigate('/podcasts')}
                className="flex items-center space-x-2 text-white/80 hover:text-white mb-6 transition-colors duration-200"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Podcasts</span>
              </button>

              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                  {series.category}
                </span>
                <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-sm">
                  {series.episodeCount} episodes
                </span>
                <div className={`px-3 py-1 ${seriesStatusInfo.color} backdrop-blur-sm rounded-full text-sm flex items-center gap-1`}>
                  {seriesStatusInfo.icon}
                  <span>{seriesStatusInfo.text}</span>
                </div>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{series.title}</h1>
              <p className="text-xl text-white/90 mb-6">{series.description}</p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <Clock className="w-5 h-5" />
                  <span>{series.totalDuration}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Headphones className="w-5 h-5" />
                  <span>{seriesStats.totalEpisodes} episodes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Music className="w-5 h-5" />
                  <span>{seriesStats.episodesReady} ready</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Star className="w-5 h-5 fill-current" />
                  <span>{series.rating}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handlePlaySeries}
                  className="flex items-center space-x-3 px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold hover:shadow-xl transition-all duration-200"
                >
                  <Play className="w-6 h-6" />
                  <span>Start Listening</span>
                </button>
                <button
                  onClick={handleShareSeries}
                  className="flex items-center space-x-3 px-8 py-4 bg-white/20 backdrop-blur-sm text-white rounded-xl font-semibold hover:bg-white/30 transition-all duration-200"
                >
                  <Share2 className="w-6 h-6" />
                  <span>Share Series</span>
                </button>
              </div>
            </div>

            <div className="relative">
              <img 
                src={series.image} 
                alt={series.title}
                className="rounded-2xl shadow-2xl w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
              
              {/* Series Progress Overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <div className="bg-black/60 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-white">Series Progress</span>
                    <span className="text-white">{seriesStats.completionPercentage}%</span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${seriesStats.completionPercentage}%` }}
                    ></div>
                  </div>
                  <div className="flex justify-between text-xs text-white/80 mt-1">
                    <span>{seriesStats.episodesReady} ready</span>
                    <span>{seriesStats.episodesComingSoon} coming soon</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Learning Path */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-md p-6 mb-8"
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center space-x-2">
                <Zap className="w-6 h-6 text-yellow-500" />
                <span>Learning Path</span>
              </h2>
              <div className="space-y-4">
                {series.learningPath.map((step, index) => {
                  const episode = seriesEpisodes[index]
                  const hasAudio = episode?.hasAudio || false
                  
                  return (
                    <div key={index} className="flex items-start space-x-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                        hasAudio ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{step}</h3>
                          {episode && getEpisodeStatusBadge(episode)}
                        </div>
                        {episode && (
                          <p className="text-sm text-gray-600">
                            Covered in: <span className={hasAudio ? "text-green-600" : "text-gray-500"}>{episode.title}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </motion.div>

            {/* Episodes List */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Episodes in Order</h2>
                <div className="text-sm text-gray-600">
                  {seriesStats.episodesReady} of {seriesStats.totalEpisodes} ready
                </div>
              </div>
              <div className="space-y-6">
                {seriesEpisodes.map((episode, index) => {
                  const isCurrent = currentPlayingEpisode?.id === episode.id
                  
                  return (
                    <motion.div
                      key={episode.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`border rounded-xl p-6 hover:shadow-lg transition-all duration-300 ${
                        isCurrent 
                          ? episode.hasAudio ? 'border-green-500 bg-green-50' : 'border-amber-500 bg-amber-50'
                          : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                              episode.hasAudio ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                episode.hasAudio 
                                  ? 'bg-green-100 text-green-700' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {episode.episodeType}
                              </span>
                              {getEpisodeStatusBadge(episode)}
                              <span className="text-sm text-gray-500">{episode.duration}</span>
                            </div>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900 mb-2">{episode.title}</h3>
                          <p className="text-gray-600 mb-4">{episode.summary}</p>
                          <div className="flex items-center space-x-4 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span>{episode.duration}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Headphones className="w-4 h-4" />
                              <span>{episode.listens?.toLocaleString()} listens</span>
                            </div>
                            <span>{episode.publishDate === "Coming Soon" ? "Coming Soon" : new Date(episode.publishDate).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex flex-col space-y-2 ml-4">
                          <button
                            onClick={() => handlePlayEpisode(episode)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors duration-200 ${
                              episode.hasAudio
                                ? 'bg-green-500 text-white hover:bg-green-600'
                                : 'bg-amber-500 text-white hover:bg-amber-600'
                            }`}
                          >
                            <Play className="w-4 h-4" />
                            <span>{episode.hasAudio ? 'Play' : 'Preview'}</span>
                          </button>
                          {episode.relatedCourseId && (
                            <button
                              onClick={() => handleCourseNavigation(episode.relatedCourseId)}
                              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                            >
                              <BookOpen className="w-4 h-4" />
                              <span>Course</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Series Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Series Overview</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Episodes</span>
                  <span className="font-semibold">{seriesStats.totalEpisodes}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Ready Now</span>
                  <div className="flex items-center gap-1">
                    <Music className="w-4 h-4 text-green-500" />
                    <span className="font-semibold text-green-600">{seriesStats.episodesReady}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Coming Soon</span>
                  <div className="flex items-center gap-1">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    <span className="font-semibold text-amber-600">{seriesStats.episodesComingSoon}</span>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Duration</span>
                  <span className="font-semibold">{series.totalDuration}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Category</span>
                  <span className="font-semibold">{series.category}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Rating</span>
                  <div className="flex items-center space-x-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-current" />
                    <span className="font-semibold">{series.rating}</span>
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="mt-6 pt-6 border-t">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Completion</span>
                  <span className="font-medium">{seriesStats.completionPercentage}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${seriesStats.completionPercentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>Ready: {seriesStats.episodesReady}</span>
                  <span>Total: {seriesStats.totalEpisodes}</span>
                </div>
              </div>
            </motion.div>

            {/* Related Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Related Content</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/courses')}
                  className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-2">
                    <BookOpen className="w-4 h-4 text-primary" />
                    <span className="font-medium">Related Courses</span>
                  </div>
                </button>
                <button 
                  onClick={() => navigate('/learning-path')}
                  className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-primary" />
                    <span className="font-medium">Learning Paths</span>
                  </div>
                </button>
              </div>
            </motion.div>

            {/* Episode Status Legend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl shadow-md p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Episode Status</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <div>
                    <div className="font-medium text-gray-900">Ready Now</div>
                    <div className="text-sm text-gray-600">Audio available, click to play</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div>
                    <div className="font-medium text-gray-900">Coming Soon</div>
                    <div className="text-sm text-gray-600">Audio not available yet</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                  <div>
                    <div className="font-medium text-gray-900">Coming Soon</div>
                    <div className="text-sm text-gray-600">Click to see preview details</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      <PodcastPlayer />
    </div>
  )
}

export default PodcastSeries